/**
 * Helper functions for creating common date preset options
 */

import { DatePresetOption } from "../components/common/DateInput";

/**
 * Get the end of the current quarter
 */
const getEndOfCurrentQuarter = (): Date => {
  const now = new Date();
  const quarter = Math.floor(now.getMonth() / 3);
  const endOfQuarter = new Date(now.getFullYear(), (quarter + 1) * 3, 0);
  return endOfQuarter;
};

/**
 * Get the end of the current calendar year
 */
const getEndOfCurrentYear = (): Date => {
  const now = new Date();
  return new Date(now.getFullYear(), 11, 31);
};

/**
 * Get the end of the next quarter
 */
const getEndOfNextQuarter = (): Date => {
  const now = new Date();
  const currentQuarter = Math.floor(now.getMonth() / 3);
  const nextQuarter = (currentQuarter + 1) % 4;
  const year = nextQuarter === 0 ? now.getFullYear() + 1 : now.getFullYear();
  const endOfNextQuarter = new Date(year, (nextQuarter + 1) * 3, 0);
  return endOfNextQuarter;
};

/**
 * Get the end of the next calendar year
 */
const getEndOfNextYear = (): Date => {
  const now = new Date();
  return new Date(now.getFullYear() + 1, 11, 31);
};

/**
 * Get the end of the current month
 */
const getEndOfCurrentMonth = (): Date => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() + 1, 0);
};

/**
 * Get the end of the next month
 */
const getEndOfNextMonth = (): Date => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() + 2, 0);
};

/**
 * Get date in 3 months from now
 */
const getIn3Months = (): Date => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() + 3, now.getDate());
};

/**
 * Get date in 6 months from now
 */
const getIn6Months = (): Date => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() + 6, now.getDate());
};

/**
 * Get date in 12 months from now
 */
const getIn12Months = (): Date => {
  const now = new Date();
  return new Date(now.getFullYear() + 1, now.getMonth(), now.getDate());
};

/**
 * Common date preset options for task due dates
 */
export const commonDatePresets: DatePresetOption[] = [
  {
    label: "In 3 months",
    getValue: getIn3Months,
  },
  {
    label: "In 6 months",
    getValue: getIn6Months,
  },
  {
    label: "In 12 months",
    getValue: getIn12Months,
  },
  {
    label: "End of this quarter",
    getValue: getEndOfCurrentQuarter,
  },
  {
    label: "End of next quarter",
    getValue: getEndOfNextQuarter,
  },
  {
    label: "End of this year",
    getValue: getEndOfCurrentYear,
  },
  {
    label: "End of next year",
    getValue: getEndOfNextYear,
  },
];

/**
 * Extended date preset options including monthly options
 */
export const extendedDatePresets: DatePresetOption[] = [
  ...commonDatePresets,
  {
    label: "End of current month",
    getValue: getEndOfCurrentMonth,
  },
  {
    label: "End of next month",
    getValue: getEndOfNextMonth,
  },
];

/**
 * Create a custom date preset option
 */
export const createDatePreset = (
  label: string,
  getValue: () => Date
): DatePresetOption => ({
  label,
  getValue,
});

/**
 * Get quarter-based presets only
 */
export const getQuarterlyPresets = (): DatePresetOption[] => [
  {
    label: "End of current quarter",
    getValue: getEndOfCurrentQuarter,
  },
  {
    label: "End of next quarter",
    getValue: getEndOfNextQuarter,
  },
];

/**
 * Get yearly presets only
 */
export const getYearlyPresets = (): DatePresetOption[] => [
  {
    label: "End of calendar year",
    getValue: getEndOfCurrentYear,
  },
  {
    label: "End of next year",
    getValue: getEndOfNextYear,
  },
];
